import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../lib/auth'

/**
 * Host bridge for the embedded Contextus widget (Epic 10).
 *
 * The widget itself is the verbatim vanilla-JS asset served by briefer-api at
 * `/static/contextus/contextus.{js,css}` (the `contextus-static` jar on the
 * classpath). Per the INTEGRATION.md contract, the host page must define
 * `window.ContextusHost` BEFORE the script loads. This component does exactly
 * that — wiring the widget to React state + auth + i18n — then injects the
 * asset tags once. It renders nothing.
 *
 * Field discovery is DOM-based: the widget anchors its "suggest" affordance to
 * `[data-key]` inputs (top-level fields) and `[data-dyn-key]` cells inside
 * `[data-dyn]` lists. The brief snapshot it sends to /suggest comes from
 * `gatherFormData()` below, which returns the live form state.
 */

type Body = Record<string, unknown>

interface ContextusHost {
  apiPath: string
  auth: { token: () => string | null; user: () => { id: string } | null }
  gatherFormData: () => Body
  getValue: (key: string) => unknown
  getDynList: (key: string) => unknown[]
  addDynItem: (key: string) => void
  i18n: { t: (key: string, fallback?: string) => string }
}

declare global {
  interface Window {
    ContextusHost?: ContextusHost
  }
}

const ASSET_ID = 'contextus-widget-script'

interface ContextusWidgetProps {
  /** Live intake form state — the brief context sent to /suggest. */
  body: Body
  /** Append an empty row to a dynamic list (row-mode "Add row"). */
  onAddDynItem: (key: string) => void
}

export function ContextusWidget({ body, onAddDynItem }: ContextusWidgetProps) {
  const { t } = useTranslation()
  // Keep the latest props in a ref so the (stable) host callbacks always read
  // current state without re-defining window.ContextusHost on every render.
  const bodyRef = useRef(body)
  bodyRef.current = body
  const addRef = useRef(onAddDynItem)
  addRef.current = onAddDynItem

  useEffect(() => {
    window.ContextusHost = {
      apiPath: '/api/v1/contextus/suggest',
      auth: {
        token: () => useAuth.getState().token,
        user: () => {
          const u = useAuth.getState().user
          return u ? { id: u.sub } : null
        },
      },
      gatherFormData: () => bodyRef.current,
      getValue: (key) => bodyRef.current[key],
      getDynList: (key) => {
        const v = bodyRef.current[key]
        return Array.isArray(v) ? v : []
      },
      addDynItem: (key) => addRef.current(key),
      i18n: { t: (key, fallback) => t(key, fallback ?? key) },
    }

    // Inject the verbatim widget assets once (after the host is defined).
    if (!document.getElementById(ASSET_ID)) {
      const css = document.createElement('link')
      css.rel = 'stylesheet'
      css.href = '/static/contextus/contextus.css'
      css.id = 'contextus-widget-css'
      document.head.appendChild(css)

      const script = document.createElement('script')
      script.src = '/static/contextus/contextus.js'
      script.id = ASSET_ID
      script.defer = true
      document.body.appendChild(script)
    }
    // The widget reads window.ContextusHost lazily on each call, so we only
    // need to (re)define it; no teardown — leaving the asset loaded across
    // intake re-mounts is intentional (avoids re-fetching the script).
  }, [t])

  return null
}
